import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";
import { toast } from "sonner";
import { LogOut, Trash2, Mail, Phone, RefreshCw, Loader2 } from "lucide-react";
import ProjectsTab from "./admin/ProjectsTab";
import SecurityTab from "./admin/SecurityTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboardPage() {
  const { user, logout, authHeaders } = useAuth();
  const { t, lang } = useLang();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("inquiries");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/inquiries`, {
        headers: authHeaders(),
        withCredentials: true,
      });
      setInquiries(data);
    } catch (err) {
      console.warn("Inquiries load failed:", err?.response?.status ?? err?.message);
      toast.error(t("admin.dash.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, t]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm(t("admin.dash.confirm_delete"))) return;
    try {
      await axios.delete(`${API}/inquiries/${id}`, {
        headers: authHeaders(),
        withCredentials: true,
      });
      setInquiries((s) => s.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success(t("admin.dash.deleted"));
    } catch (err) {
      console.warn("Inquiry delete failed:", err?.response?.status ?? err?.message);
      toast.error(t("admin.dash.delete_failed"));
    }
  };

  const localeMap = { de: "de-DE", en: "en-US", ru: "ru-RU" };
  const locale = localeMap[lang] || "de-DE";

  return (
    <div data-testid="admin-dashboard" className="pt-32 pb-20">
      <PageMeta pageKey="admin" />
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 pb-8 border-b border-white/10">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-3">{t("admin.dash.eyebrow")}</p>
            <h1 className="font-display text-4xl md:text-5xl font-light uppercase tracking-tight leading-tight">
              {t("admin.dash.title")}
            </h1>
            <p className="text-sm text-white/60 mt-3">
              {t("admin.dash.signed_as")} <span className="text-white">{user?.email}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 hover:border-white text-xs tracking-[0.18em] uppercase font-medium transition-colors"
            >
              <RefreshCw size={14} strokeWidth={1.5} />
              {t("admin.dash.refresh")}
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-5 py-3 bg-white text-[#051630] text-xs tracking-[0.18em] uppercase font-bold hover:bg-transparent hover:text-white border border-white transition-colors"
            >
              <LogOut size={14} strokeWidth={2} />
              {t("admin.dash.logout")}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-10" data-testid="admin-tabs">
          <button
            onClick={() => setTab("inquiries")}
            data-testid="admin-tab-inquiries"
            className={`px-5 py-3 text-xs tracking-[0.18em] uppercase font-medium border transition-colors ${
              tab === "inquiries" ? "bg-white text-[#051630] border-white" : "border-white/20 text-white/70 hover:border-white/50"
            }`}
          >
            {t("admin.tabs.inquiries")}
          </button>
          <button
            onClick={() => setTab("projects")}
            data-testid="admin-tab-projects"
            className={`px-5 py-3 text-xs tracking-[0.18em] uppercase font-medium border transition-colors ${
              tab === "projects" ? "bg-white text-[#051630] border-white" : "border-white/20 text-white/70 hover:border-white/50"
            }`}
          >
            {t("admin.tabs.projects")}
          </button>
          <button
            onClick={() => setTab("security")}
            data-testid="admin-tab-security"
            className={`px-5 py-3 text-xs tracking-[0.18em] uppercase font-medium border transition-colors ${
              tab === "security" ? "bg-white text-[#051630] border-white" : "border-white/20 text-white/70 hover:border-white/50"
            }`}
          >
            {t("admin.tabs.security")}
          </button>
        </div>

        {tab === "projects" ? (
          <ProjectsTab />
        ) : tab === "security" ? (
          <SecurityTab />
        ) : (
        <>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-white/10 mb-12">
          <Stat label={t("admin.dash.stat_total")} value={inquiries.length} />
          <Stat label={t("admin.dash.stat_week")} value={inquiries.filter(filterRecent(7)).length} />
          <Stat label={t("admin.dash.stat_today")} value={inquiries.filter(filterRecent(1)).length} />
          <Stat label={t("admin.dash.stat_phone")} value={inquiries.filter((i) => !!i.phone).length} />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-white/50">
            <Loader2 className="animate-spin mr-3" size={20} /> {t("admin.dash.loading")}
          </div>
        ) : inquiries.length === 0 ? (
          <div className="border border-white/10 p-16 text-center">
            <p className="text-white/60">{t("admin.dash.empty")}</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-8">
            {/* List */}
            <div className="lg:col-span-7 border border-white/10 overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-inquiries-table">
                <thead className="border-b border-white/10">
                  <tr className="text-[10px] tracking-[0.25em] uppercase text-white/50">
                    <th className="text-left p-4 font-medium">{t("admin.dash.col_name")}</th>
                    <th className="text-left p-4 font-medium hidden md:table-cell">{t("admin.dash.col_email")}</th>
                    <th className="text-left p-4 font-medium hidden lg:table-cell">{t("admin.dash.col_size")}</th>
                    <th className="text-left p-4 font-medium">{t("admin.dash.col_date")}</th>
                    <th className="text-right p-4 font-medium">{t("admin.dash.col_action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {inquiries.map((i) => (
                    <tr
                      key={i.id}
                      onClick={() => setSelected(i)}
                      data-testid={`inquiry-row-${i.id}`}
                      className={`border-b border-white/5 cursor-pointer transition-colors ${
                        selected?.id === i.id ? "bg-white/[0.05]" : "hover:bg-white/[0.02]"
                      }`}
                    >
                      <td className="p-4">
                        <p className="text-white">
                          {i.first_name} {i.last_name}
                        </p>
                      </td>
                      <td className="p-4 text-white/60 hidden md:table-cell">{i.email}</td>
                      <td className="p-4 text-white/60 hidden lg:table-cell">
                        {i.residence_size || "—"}
                      </td>
                      <td className="p-4 text-white/60 text-xs">
                        {new Date(i.created_at).toLocaleDateString(locale, {
                          day: "2-digit",
                          month: "2-digit",
                          year: "2-digit",
                        })}
                      </td>
                      <td className="p-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(i.id);
                          }}
                          data-testid={`delete-inquiry-${i.id}`}
                          className="inline-flex items-center justify-center w-8 h-8 border border-white/15 hover:border-white hover:bg-white hover:text-[#051630] transition-colors"
                          aria-label="Delete"
                        >
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Detail */}
            <div className="lg:col-span-5">
              {selected ? (
                <div className="border border-white/10 p-8 sticky top-28" data-testid="inquiry-detail">
                  <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-3">
                    {t("admin.dash.detail_eyebrow")}
                  </p>
                  <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight mb-6">
                    {selected.first_name} {selected.last_name}
                  </h3>
                  <div className="space-y-4 text-sm">
                    <DetailRow label={t("common.email")}>
                      <a
                        href={`mailto:${selected.email}`}
                        className="inline-flex items-center gap-2 text-white hover:underline"
                      >
                        <Mail size={14} strokeWidth={1.5} /> {selected.email}
                      </a>
                    </DetailRow>
                    {selected.phone && (
                      <DetailRow label={t("admin.dash.detail_phone")}>
                        <a
                          href={`tel:${selected.phone}`}
                          className="inline-flex items-center gap-2 text-white hover:underline"
                        >
                          <Phone size={14} strokeWidth={1.5} /> {selected.phone}
                        </a>
                      </DetailRow>
                    )}
                    {selected.residence_size && (
                      <DetailRow label={t("admin.dash.detail_size")}>{selected.residence_size}</DetailRow>
                    )}
                    {selected.price_range && (
                      <DetailRow label={t("admin.dash.detail_price")}>{selected.price_range}</DetailRow>
                    )}
                    <DetailRow label={t("admin.dash.detail_received")}>
                      {new Date(selected.created_at).toLocaleString(locale)}
                    </DetailRow>
                    {selected.message && (
                      <div className="pt-4 border-t border-white/10">
                        <p className="text-[11px] tracking-[0.25em] uppercase text-white/50 mb-3">
                          {t("admin.dash.detail_message")}
                        </p>
                        <p className="text-white/80 leading-relaxed whitespace-pre-line">
                          {selected.message}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="border border-white/10 border-dashed p-12 text-center text-white/40 text-sm">
                  {t("admin.dash.empty_select")}
                </div>
              )}
            </div>
          </div>
        )}
        </>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-b border-r border-white/10 p-6 md:p-8">
      <p className="font-display text-3xl md:text-4xl font-light tracking-tight">{value}</p>
      <p className="text-[11px] tracking-[0.25em] uppercase text-white/50 mt-2">{label}</p>
    </div>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-white/5 pb-3">
      <span className="text-[11px] tracking-[0.25em] uppercase text-white/50 shrink-0">
        {label}
      </span>
      <span className="text-right text-white/85">{children}</span>
    </div>
  );
}

function filterRecent(days) {
  const threshold = Date.now() - days * 24 * 60 * 60 * 1000;
  return (i) => new Date(i.created_at).getTime() > threshold;
}
